import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { T, TF } from "react-translator-component";
import GCBackButton from "../GreenCity/GCBackButton";
import LoadingModal from "../LoadingModal";
import MessageModal from "../Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../ErrorMessageModal";
import TwoButtonModal from "../TwoButtonModal";
import RefundEventApplicationDetail from "./RefundEventApplicationDetail";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import dayjs from "../../util/dayjs";
import appServices from "../../services/app-services/appServices";
import { REFUND } from "../../util/CreatePost/utils";

const UserEventCancellation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postEventUserId } = location.state || {};

  const [postDetail, setPostDetail] = useState<any>();
  const [userDetail, setUserDetail] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [eventUserDetail, setEventUserDetail] = useState<any>();
  const [disabledCancelBtn, setDisabledCancelBtn] = useState<boolean>(true);

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [openModalMessage, setOpenModalMessage] = useState<string>("");

  const postEvent = postDetail?.post_events[0]
  const fixedJsonString = eventUserDetail?.application_details ?
    eventUserDetail.application_details.replace(/=>/g, ":").replace(/nil/g, "null") : '[]'; // Convert Ruby-style to JSON
  const paymentDetails = JSON.parse(fixedJsonString);

  useEffect(() => {
    window.scrollTo(0, 0);

    getUserPaymentDetail();
  }, []);

  useEffect(() => {
    if (!eventUserDetail) return
    getEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUserDetail]);

  const applicantTotalAmount = () => {
    let totalAmount = 0;

    paymentDetails.forEach((payDetail: any) => {
      totalAmount += payDetail.total_amount
    })

    return totalAmount
  }

  const getUserPaymentDetail = async () => {
    try {
      setLoading(true);
      // 最新の申し込み内容を取得する
      const response = await appServices.getSinglePostEventUsers(postEventUserId);
      setEventUserDetail(response?.post_event_user);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getEventDetails = async () => {
    try {
      setLoading(true);
      const res = await appServices.getSingleEventById(eventUserDetail?.post_id);
      const post = res?.post
      setPostDetail(post);
      const response = await appServices.getSingleUser(eventUserDetail?.user_id);
      setUserDetail(response?.user);

      const today = dayjs().startOf("day");
      const eventEnd = dayjs(post?.post_events[0]?.end_date).startOf("day");
      const EventDateRemains =
        eventEnd.isSame(today, "day") || eventEnd.isAfter(today);

      setDisabledCancelBtn(!EventDateRemains);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    setShowConfirmationModal(false);

    try {
      setLoading(true);

      let updateStatus: string = '';
      let successMessage: string = '';
      const eventFeeAmount = applicantTotalAmount()
      if (paymentDetails?.length > 0 && eventFeeAmount > 0 && postEvent?.application_cancellation_rule === REFUND.enabled) {
        updateStatus = "Cancel-pending"
        successMessage = `${T("event_payment_details_pending_message")}`
      } else {
        updateStatus = "Cancelled"
        successMessage = `${T("event_payment_details_canceled_message")}`
      }

      if (!updateStatus) return false

      const body = {
        status: updateStatus
      };

      await appServices.updatePostEventUsers(eventUserDetail?.id, body);
      setOpenModalMessage(successMessage);
      setShowSuccessModal(true);
      return true;
    } catch (error: any) {
      setOpenModalMessage(error?.response?.data?.message ?? T("api_unknown_error"))
      console.error(error);
      setShowErrorModal(true);
      return false
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMessageModal = () => {
    if (showSuccessModal) {
      setShowSuccessModal(false);
      navigate("/myfarm");
    } else {
      setShowErrorModal(false);
    }
  }

  return (
    <>
      <Box>
        <LoadingModal visible={loading} />

        <div style={styles.root}>
          <GCBackButton onClick={() => navigate(-1)} />

          <RefundEventApplicationDetail
            post={postDetail}
            applier={userDetail}
            paymentDetails={paymentDetails}
          />

          <Typography sx={styles.title}>
            {T("change_number_of_entering_person")}
          </Typography>
          <Typography>
            {`${T("event_payment_details_cannot_change_number_of_persons")}`}
          </Typography>
          <Typography sx={styles.title}>
            {TF(T("parentheses_with_corner"), T("about_cancel"), "")}
          </Typography>
          <Typography>
            {T("event_payment_details_description_of_cancel_1")}
          </Typography>
          <Typography>
            {T("event_payment_details_description_of_cancel_2")}
          </Typography>
          <Typography sx={styles.title}>
            {T("about_cancel_fee")}
          </Typography>
          <Typography>
            {postEvent?.application_cancellation_rule}
          </Typography>

          <Button
            color="secondary"
            sx={styles.submitButton}
            size={"large"}
            disabled={disabledCancelBtn || eventUserDetail?.status === "Cancel-pending"
            }
            onClick={() => setShowConfirmationModal(true)}
            variant="outlined"
            endIcon={<ArrowForwardIosIcon sx={styles.arrowIcon}/>}
          >
            {eventUserDetail?.status === "Cancel-pending" ? T("cancel_pending") : T("execute_cancel")}
          </Button>
        </div>
      </Box>

      <TwoButtonModal
        open={showConfirmationModal}
        message={`${T("event_payment_details_confirm_cancel")}`}
        showButton={true}
        onContinue={cancel}
        onClose={() => setShowConfirmationModal(false)}
      />

      <MessageModal
        open={showSuccessModal}
        message={openModalMessage}
        onContinue={() => handleCloseMessageModal()}
        onClose={() => handleCloseMessageModal()}
        showButton={true}
        buttonText={T("close")}
      />

      <ErrorMessageModal
        open={showErrorModal}
        messages={[openModalMessage]}
        showButton={true}
        onClose={() => handleCloseMessageModal()}
        alignLeft={openModalMessage.indexOf("\n") !== -1}
      />
    </>
  );
};

const styles = {
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    marginTop: "20px",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
  },
  title: {
    borderBottom: "2px solid #86BB57",
    paddingTop: "20px",
    marginBottom: "10px",
    textAlign: "left",
    fontSize: "15px",
  },
  arrowIcon: {
    position: "absolute",
    right: 0,
    top: 0,
    margin: "10px",
  },
  root: {
    paddingTop: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: "12px",
  }
};

export default UserEventCancellation;
